import {
  newIdempotencyKey,
  markPoolItemAsOpened,
  ApiError,
  type ApiClient,
  type IframePoolOrderItem,
  type VolatilityLevel,
} from '../api';
import { DEMO_ORDER_ID } from './demoSource';
import { instantPurchaseWithFairness } from '../fairness/purchase';
import { isSpendingLimitCode, SPENDING_LIMIT_MESSAGE } from '../game/LootBoxOpening/purchaseErrorCopy';

/**
 * Outcome of {@link buyAndOpenNextPack}. The reel + WinnerStep branch on `kind`:
 *   • success            → land the reel on the real pull.
 *   • pre_purchase_error → money was NOT taken; safe to abort + offer a full retry.
 *   • post_purchase_error→ money WAS taken (purchase succeeded) but we failed to
 *     open the pull. MUST NOT re-buy (double charge) and MUST NOT fake a card.
 */
export type NextPackOutcome =
  | { kind: 'success'; item: IframePoolOrderItem; orderId: string }
  | { kind: 'pre_purchase_error'; message: string; orderId: string | null; code?: string }
  | { kind: 'post_purchase_error'; message: string; orderId: string };

export interface NextPackPurchaseHooks {
  onPurchaseInitiated: (orderId: string) => void;
  onPurchaseCompleted: (
    orderId: string,
    items: { id: string; name: string; rarity: string }[],
  ) => void;
  onItemRevealed: (
    orderId: string,
    itemId: string,
    title: string,
    fairMarketValue: number | null,
  ) => void;
  onWalletRefresh: () => void;
}

function errMessage(err: unknown): string {
  return err instanceof Error ? err.message : 'Failed to open next pack';
}

/**
 * Buys + opens the next pack for `offerId`. The purchase is one atomic
 * `instant_purchase` (create + charge in a single round-trip); the pull is then
 * opened with a separate `mark_as_opened` so the reel animates off a freshly
 * opened card exactly like the in-page flow. NEVER throws — every failure is
 * mapped to a typed {@link NextPackOutcome} so the caller decides UI without a
 * try/catch. The two nested try/catch blocks split pre- vs post-purchase
 * failure: once `instant_purchase` resolves the money is committed, so any later
 * failure is `post_purchase_error`, never a safe-to-retry one. A fresh
 * idempotency key is minted per call; the in-place "Try Again" remounts and
 * calls this again, which is a NEW attempt. `tellsFacts` is whether the mechanic
 * the loop plays tells the card's facts: the pull is opened for that mechanic,
 * even if the purchase brought a newer override of the pack.
 */
export async function buyAndOpenNextPack(
  client: ApiClient,
  offerId: string,
  hooks: NextPackPurchaseHooks,
  volatility: VolatilityLevel = 'normal',
  tellsFacts?: boolean,
): Promise<NextPackOutcome> {
  let orderId: string | null = null;
  try {
    const order = await instantPurchaseWithFairness(client, offerId, 1, volatility, newIdempotencyKey());
    orderId = order.id;
    hooks.onPurchaseInitiated(order.id);
    // ── money committed from here on ──
    try {
      hooks.onPurchaseCompleted(
        order.id,
        order.poolItems.map((i) => ({
          id: i.id,
          name: i.title,
          rarity: i.badge?.name ?? 'common',
        })),
      );
      hooks.onWalletRefresh();
      const poolItem = order.poolItems[0];
      if (!poolItem) throw new Error('New order has no item');
      const opened = await markPoolItemAsOpened(client, poolItem.id, offerId, tellsFacts);
      hooks.onItemRevealed(
        order.id,
        String(opened.id),
        opened.title,
        opened.fairMarketValue,
      );
      return { kind: 'success', item: opened, orderId: order.id };
    } catch (err) {
      return { kind: 'post_purchase_error', message: errMessage(err), orderId: order.id };
    }
  } catch (err) {
    const apiCode = err instanceof ApiError ? err.code : undefined;
    return {
      kind: 'pre_purchase_error',
      message: errMessage(err),
      orderId,
      ...(apiCode !== undefined ? { code: apiCode } : {}),
    };
  }
}

/**
 * Demo loop: a free pull stands in for the purchase. Returns the same typed
 * outcome so the reel / scene machinery branches exactly as it does for a
 * real buy. A failure is always `pre_purchase_error` — no money was ever
 * involved, so the error panel's Try Again is always safe.
 */
export async function pullDemoNextPack(
  pullCard: () => Promise<IframePoolOrderItem>,
): Promise<NextPackOutcome> {
  try {
    const item = await pullCard();
    return { kind: 'success', item, orderId: DEMO_ORDER_ID };
  } catch (err) {
    return { kind: 'pre_purchase_error', message: errMessage(err), orderId: null };
  }
}

export interface NextPackErrorCopy {
  headline: string;
  body: string;
  /** Pre-purchase failures can be safely retried (re-buy). Post-purchase
   *  failures must not (the pack is already paid + opened server-side). */
  canRetry: boolean;
}

/**
 * Maps a failed outcome to user-facing copy. Pre-purchase shows the backend
 * message (e.g. "insufficient balance") + a retry; post-purchase reassures the
 * user the pack is safe and points them to My Packs (no retry, no re-charge).
 */
export function nextPackErrorCopy(
  outcome: Extract<
    NextPackOutcome,
    { kind: 'pre_purchase_error' | 'post_purchase_error' }
  >,
): NextPackErrorCopy {
  if (outcome.kind === 'post_purchase_error') {
    return {
      headline: 'Pack opened',
      body: "Your pack was opened but we couldn't show it here. It's safe in My Packs.",
      canRetry: false,
    };
  }
  // The operator's wallet refused over the player's spending limit: the
  // message is the operator's own, and a retry would only be refused again.
  if (isSpendingLimitCode(outcome.code)) {
    return {
      headline: 'Spending limit reached',
      body: outcome.message || SPENDING_LIMIT_MESSAGE,
      canRetry: false,
    };
  }
  return {
    headline: "Couldn't open the pack",
    body: outcome.message || 'Something went wrong. Please try again.',
    canRetry: true,
  };
}
